import { useEffect } from "react"

import { useQuery } from "@tanstack/react-query"

import { useAuth } from "src/context/AuthContext"
import { useChannelLink } from "src/context/ChannelLinkContext"

/**
 * Fetches public channel branding for a channel ID without requiring authentication.
 * Calls GET /channel_info/{channelId} on the auth server.
 * Used on the login screen and channel-access-denied screen when arriving via a deeplink.
 *
 * If the channel is not found the pending channel ID is cleared, reverting the login
 * screen to its standard (non-deeplink) state.
 */
export function useUnauthChannelInfoQuery(channelId: string | null) {
  const { authApi } = useAuth()
  const { clearPendingChannelId } = useChannelLink()

  const query = useQuery({
    queryKey: ["unauth", "channel_info", channelId],
    queryFn: async () => {
      const response = await authApi.getChannelInfo(channelId!)
      if (response.kind === "not-found") {
        return null
      }
      if (response.kind !== "ok") {
        throw new Error(`Failed to fetch channel info: ${response.kind}`)
      }
      return response.data
    },
    enabled: !!channelId,
    staleTime: 0,
    retry: false,
  })

  useEffect(() => {
    if (query.isSuccess && query.data === null) {
      clearPendingChannelId()
    }
  }, [query.isSuccess, query.data, clearPendingChannelId])

  return query
}
